"use client";

import { useEffect, useRef, useState } from "react";

const STORAGE_KEY = "pc:recently-viewed";
const MAX_ITEMS = 25;
const TTL_MS = 30 * 24 * 60 * 60 * 1000;

interface StoredSku {
	sku: string;
	timestamp: number;
}

export interface UseRecentlyViewedResult {
	skus: string[];
}

/**
 * Manages recently viewed product SKUs in localStorage.
 *
 * - If `currentSku` is provided, records it (deduplicates, bumps timestamp).
 * - Evicts items older than 30 days.
 * - LRU-evicts when exceeding 25 items.
 * - Returns SKU list excluding `currentSku` (sorted by most recent first).
 */
export function useRecentlyViewed(
	currentSku?: string
): UseRecentlyViewedResult {
	const [skus, setSkus] = useState<string[]>([]);
	const recorded = useRef(false);

	useEffect(() => {
		let items = readStorage();

		if (currentSku && !recorded.current) {
			recorded.current = true;
			items = items.filter((i) => i.sku !== currentSku);
			items.unshift({ sku: currentSku, timestamp: Date.now() });
		}

		items = evictStaleItems(items);

		writeStorage(items);

		setSkus(items.map((i) => i.sku).filter((s) => s !== currentSku));
	}, [currentSku]);

	return { skus };
}

function evictStaleItems(items: StoredSku[]): StoredSku[] {
	const cutoff = Date.now() - TTL_MS;
	return items.filter((i) => i.timestamp > cutoff).slice(0, MAX_ITEMS);
}

function readStorage(): StoredSku[] {
	try {
		const raw = localStorage.getItem(STORAGE_KEY);
		if (!raw) return [];
		const parsed = JSON.parse(raw);
		if (!Array.isArray(parsed)) return [];
		return parsed;
	} catch {
		return [];
	}
}

function writeStorage(items: StoredSku[]): void {
	try {
		localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
	} catch {
		// Storage full or unavailable — silently ignore
	}
}
